import {
  Alert,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isEmpty, isNil } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  RateType,
  TariffGroupType,
  TariffType,
  TariffZoneType,
  useFuelProfilesQuery,
  useMeQuery,
  useServicesQuery,
  useShallowContactsQuery,
  useTariffZoneGroupsQuery,
  useVehicleTypesQuery,
} from "../../../generated/graphql";
import { useImportTariffForm } from "../../tariff-importer/use-import-tariff-form";
import { Wizard, type WizardStep } from "../wizard/wizard";
import { ReviewStep } from "./review-step";
import {
  renderDefinitionStep,
  renderScheduleFuelStep,
  renderSourceStep,
  renderTargetingStep,
} from "./steps";
import { useLineHaulNetworksEnabled } from "./use-line-haul-networks-enabled";

const SDK_KEY = process.env.REACT_APP_STATSIG_SDK_KEY ?? "";
const ENVIRONMENT = process.env.REACT_APP_APP_ENV;

const TARIFF_CHAINS_GATE = "tariff_chains_enabled";

const styles = {
  container: { padding: 4 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Wizard-based container for the Crown tariff importer.
 *
 * Owns the tariff form, all supporting data queries (current user/company,
 * contacts, tariff zone groups, services, vehicle types, fuel profiles), the
 * `tariff_chains_enabled` Statsig gate fetch, and the derived option lists. It
 * presents the import as a five-step {@link Wizard} (source, targeting,
 * definition, schedule/fuel, review) instead of a single flat form, while
 * preserving the existing import behavior. The review step gates the commit on
 * an optional dry-run preview and, in production, a typed confirmation.
 *
 * Side effects: fetches the `tariff_chains_enabled` gate from the Statsig HTTP
 * API and runs the tariff import mutation (via the review step).
 */
const TariffWizard = () => {
  const { data: meData } = useMeQuery({
    fetchPolicy: "cache-first",
  });
  const { data: contactsData } = useShallowContactsQuery();
  const { data: tariffZoneGroupsData } = useTariffZoneGroupsQuery({
    variables: {
      findTariffZoneGroupsInput: {
        isArchived: false,
      },
    },
  });
  const { form } = useImportTariffForm();
  const {
    reset,
    control,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = form;

  const [successVisible, setSuccessVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const companyUuid = meData?.me?.company?.uuid ?? "";
  const companyName = meData?.me?.company?.name ?? "";
  const tariffChainsEnabled: boolean = watch("tariffChainsEnabled");

  const { enabled: dryRunSupported } = useLineHaulNetworksEnabled(companyUuid);

  useEffect(() => {
    reset({
      contactUuids: [],
      serviceUuids: [],
      vehicleTypeUuids: [],
      autoSelectAllServices: false,
      autoSelectAllVehicleTypes: false,
      name: "",
      tariffZoneType: TariffZoneType.Location,
      tariffType: TariffType.PerHundredPounds,
      rateType: RateType.Flat,
      tariffUrl: "",
      tariffGroupType: TariffGroupType.Ordinary,
      useActualWeight: true,
      startDate: "2022-01-01",
      endDate: "2030-01-01",
      fuelProfileUuid: null,
      tariffZoneGroupId: null,
    });
  }, [reset]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // This is jank af but I'm not standing up statsig properly
        // to check for one feature flag.
        setLoading(true);
        const response = await fetch("https://api.statsig.com/v1/check_gate", {
          method: "POST",
          headers: {
            "statsig-api-key": SDK_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user: {
              userID: "",
              custom: { companyUuid },
              statsigEnvironment: { tier: ENVIRONMENT },
            },
            gateNames: [TARIFF_CHAINS_GATE],
          }),
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data: unknown = await response.json();
        const gateValue = (
          data as Record<string, { value?: boolean } | undefined>
        )?.[TARIFF_CHAINS_GATE]?.value;
        setValue("tariffChainsEnabled", gateValue ?? false);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [companyUuid, setValue]);

  const contactUuids = watch("contactUuids");
  const autoSelectAllServices = watch("autoSelectAllServices");
  const autoSelectAllVehicleTypes = watch("autoSelectAllVehicleTypes");
  const tariffType = watch("tariffType");
  const tariffGroupType = watch("tariffGroupType");
  const tariffZoneType = watch("tariffZoneType");

  const tariffCanHaveZoneGroup = useMemo(() => {
    // Zone groups are only applicable for ordinary, zone-based tariffs
    // (They're also applicable for zone-based point-to-point tariffs, but
    // these cannot currently be created in the UI to begin with, so that's
    // not yet a concern).
    if (
      tariffGroupType !== TariffGroupType.Ordinary &&
      tariffGroupType !== TariffGroupType.PointToPoint
    ) {
      return false;
    }
    // Zone groups are only applicable for zone-based tariffs
    if (tariffZoneType !== TariffZoneType.Location) {
      return false;
    }
    return true;
  }, [tariffGroupType, tariffZoneType]);

  useEffect(() => {
    if (!tariffCanHaveZoneGroup) {
      setValue("tariffZoneGroupId", null);
    }
  }, [tariffCanHaveZoneGroup, setValue]);

  const { data: fuelProfilesData } = useFuelProfilesQuery();

  const { data: globalServicesData } = useServicesQuery({
    fetchPolicy: "cache-first",
  });
  const currentServices = useMemo(() => {
    return (globalServicesData?.services ?? [])
      .filter((service) => {
        return service.isActive;
      })
      .map((service) => {
        return {
          name: service.name,
          uuid: service.uuid,
        };
      });
  }, [globalServicesData?.services]);

  const { data: globalVehicleTypesData } = useVehicleTypesQuery({
    fetchPolicy: "cache-first",
  });
  const currentVehicleTypes = useMemo(() => {
    return (globalVehicleTypesData?.vehicleTypes ?? []).map((vehicleType) => {
      return {
        name: vehicleType.name,
        uuid: vehicleType.uuid,
      };
    });
  }, [globalVehicleTypesData?.vehicleTypes]);

  useEffect(() => {
    setValue("serviceUuids", []);
    if (isEmpty(contactUuids)) {
      setValue("autoSelectAllServices", false);
    }
  }, [contactUuids, setValue]);

  useEffect(() => {
    if (autoSelectAllServices) {
      setValue("serviceUuids", []);
    }
  }, [autoSelectAllServices, setValue]);

  useEffect(() => {
    if (autoSelectAllVehicleTypes) {
      setValue("vehicleTypeUuids", []);
    }
  }, [autoSelectAllVehicleTypes, setValue]);

  useEffect(() => {
    if (isNil(tariffType) || tariffType === TariffType.NoUnits) {
      setValue("rateType", RateType.Flat);
    }
  }, [tariffType, setValue]);

  const contactOptions = useMemo(
    () =>
      contactsData?.contacts?.map((contact) => {
        return {
          value: contact.uuid,
          label: contact.displayName,
        };
      }) ?? [],
    [contactsData?.contacts],
  );

  const values = watch();
  const fuelProfiles = fuelProfilesData?.fuelProfiles ?? [];
  const tariffZoneGroups = tariffZoneGroupsData?.tariffZoneGroups ?? [];

  const steps: WizardStep[] = [
    {
      id: "source",
      label: "Source",
      validate: async () => trigger(["tariffUrl"]),
      render: () => renderSourceStep({ control }),
    },
    {
      id: "targeting",
      label: "Targeting",
      validate: async () =>
        trigger([
          "contactUuids",
          "serviceUuids",
          "vehicleTypeUuids",
          "autoSelectAllServices",
          "autoSelectAllVehicleTypes",
        ]),
      render: () =>
        renderTargetingStep({
          control,
          errors,
          tariffChainsEnabled,
          contactUuids,
          autoSelectAllServices,
          autoSelectAllVehicleTypes,
          contactOptions,
          currentServices,
          currentVehicleTypes,
        }),
    },
    {
      id: "definition",
      label: "Definition",
      validate: async () =>
        trigger([
          "name",
          "tariffGroupType",
          "tariffZoneType",
          "tariffType",
          "rateType",
          "tariffZoneGroupId",
          "useActualWeight",
        ]),
      render: () =>
        renderDefinitionStep({
          control,
          errors,
          tariffType,
          tariffCanHaveZoneGroup,
          tariffZoneGroups,
        }),
    },
    {
      id: "schedule-fuel",
      label: "Schedule & fuel",
      validate: async () =>
        trigger(["startDate", "endDate", "fuelProfileUuid"]),
      render: () =>
        renderScheduleFuelStep({
          control,
          errors,
          fuelProfiles,
        }),
    },
    {
      id: "review",
      label: "Review",
      validate: () => true,
      render: () => (
        <ReviewStep
          companyName={companyName}
          dryRunSupported={dryRunSupported}
          validateAll={async () => trigger()}
          values={values}
          onImported={() => {
            setSuccessVisible(true);
          }}
        />
      ),
    },
  ];

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Grid container spacing={1} sx={styles.container}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={successVisible}
      >
        <Alert
          severity="success"
          onClose={() => {
            setSuccessVisible(false);
          }}
        >
          Successfully imported tariff
        </Alert>
      </Snackbar>
      <Grid item xs={12}>
        <Typography variant="h4">Crown Tariff Importer</Typography>
        <Typography variant="subtitle1">
          PLEASE MAKE SURE YOU LOGGED IN WITH THE COMPANY ACCOUNT THAT YOU WANT
          TO CREATE THE TARIFF FOR
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Wizard steps={steps} />
      </Grid>
    </Grid>
  );
};

export default TariffWizard;
